import { BedDouble, Bath, Square, Home, Star } from "lucide-react";
import { useAppContext } from "../../context/AppContext";

const FALLBACK_IMGS = [
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80",
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=600&q=80",
  "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80",
];

export default function ProjectOverview({ p }) {
  const { lang } = useAppContext();

  const specs = [
    { icon: <BedDouble size={17} />, label: lang === "en" ? "Bedrooms" : "غرف", value: p.rooms },
    { icon: <Bath size={17} />,      label: lang === "en" ? "Bathrooms" : "حمامات", value: p.baths },
    { icon: <Square size={17} />,    label: lang === "en" ? "Area" : "المساحة", value: `${p.area} m²` },
    { icon: <Home size={17} />,      label: lang === "en" ? "Type" : "النوع", value: p.type },
  ];

  const imgs = p.photos?.length >= 3
    ? p.photos.slice(0, 3)
    : [p.img || FALLBACK_IMGS[0], FALLBACK_IMGS[1], FALLBACK_IMGS[2]];

  return (
    <section id="pd-overview" className="pd-section">
      <div className="pd-2col">
        {/* Left: text */}
        <div className="fadeUp">
          <span className="pd-section-label">
            {lang === "en" ? "About the Project" : "عن المشروع"}
          </span>
          <h2 className="pd-section-title">
            {lang === "en" ? "A New Standard of Living" : "معيار جديد للمعيشة"}
          </h2>
          <div className="pd-section-bar" />

          {/* Rating */}
          {p.rating > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: "1.5rem" }}>
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  fill={i < Math.round(p.rating) ? "var(--gold)" : "none"}
                  color="var(--gold)"
                  strokeWidth={1.5}
                />
              ))}
              <span
                style={{
                  fontFamily: "var(--serif)",
                  fontSize: "1rem",
                  color: "var(--navy)",
                  fontWeight: 300,
                }}
              >
                {p.rating} / 5.0
              </span>
            </div>
          )}

          <p
            style={{
              color: "var(--gray)",
              lineHeight: 2,
              fontSize: ".92rem",
              marginBottom: "2rem",
              fontFamily: "var(--sans)",
            }}
          >
            {p.description || (lang === "en" ? "Premium residential project offering world-class amenities and exceptional quality of life." : "مشروع سكني راقٍ يوفر مرافق عالمية المستوى وجودة حياة استثنائية.")}
          </p>

          {/* Notes */}
          {p.notes?.length > 0 && (
            <ul
              style={{
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: ".6rem",
                marginBottom: "2rem",
              }}
            >
              {p.notes.map((note, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: ".75rem",
                    color: "var(--navy)",
                    fontSize: ".88rem",
                    lineHeight: 1.65,
                  }}
                >
                  <span style={{ color: "var(--gold)", flexShrink: 0, marginTop: 4, fontSize: ".6rem" }}>◆</span>
                  {note}
                </li>
              ))}
            </ul>
          )}

          {/* Specs grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: ".75rem",
            }}
          >
            {specs.map((s, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "1px solid rgba(201,168,76,.12)",
                  borderRadius: 10,
                  padding: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: ".85rem",
                }}
              >
                <span style={{ color: "var(--gold)" }}>{s.icon}</span>
                <div>
                  <div
                    style={{
                      fontSize: ".58rem",
                      color: "var(--gray)",
                      textTransform: "uppercase",
                      letterSpacing: ".12em",
                      fontFamily: "var(--sans)",
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontWeight: 600,
                      color: "var(--navy)",
                      fontSize: ".92rem",
                      marginTop: 2,
                    }}
                  >
                    {s.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: image grid */}
        <div className="fadeUp d2">
          {/* Primary image */}
          <div
            className="ci"
            style={{
              borderRadius: 14,
              overflow: "hidden",
              paddingTop: "62%",
              position: "relative",
              marginBottom: ".75rem",
              boxShadow: "0 16px 48px rgba(10,22,40,.12)",
            }}
          >
            <img
              src={imgs[0]}
              alt={lang === "ar" ? p.nameAr : p.name}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
          {/* Two smaller images */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".75rem" }}>
            {[1, 2].map((idx) => (
              <div
                key={idx}
                className="ci"
                style={{
                  borderRadius: 10,
                  overflow: "hidden",
                  paddingTop: "65%",
                  position: "relative",
                  boxShadow: "0 8px 24px rgba(10,22,40,.08)",
                }}
              >
                <img
                  src={imgs[idx]}
                  alt=""
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
